// Staff-impersonation frontend constants + helpers (M7-365).
//
// Auth during impersonation rides the existing httpOnly session cookie — the
// SPA never reads, stores, or sends an impersonation token. The backend
// (M7-384) drives every "session over" outcome by returning an absolute
// `location` to navigate to (the logout 200 body and the lapse-401 body both
// carry `{ ended: true, location }`). This module only holds the fallback used
// when, for any reason, that destination is absent, plus the landing-page
// error copy keyed off the exchange error codes.

// Where to send the browser when impersonation ends and the backend did not
// supply a destination. RailsAdmin lives at /admin on the API host (a different
// origin from the SPA), so this must be an absolute URL.
export const IMPERSONATION_EXIT_FALLBACK_URL = 'https://www.minute7.com/admin';

// Non-null sentinel parked in `appStore.session_id` after a successful exchange.
// The SPA gates `isLoggedIn` on `!isNil(session_id)` (Router/App), but the
// impersonation handoff arrives with NO JWT in JS — auth is the httpOnly cookie
// the exchange set. We therefore establish the session with this marker as the
// id and a NULL `session_name`, which makes `addSessionParams` skip the
// `IMPRESARIO` query-param Bearer entirely (HelperFunctions only appends it when
// both id and name are non-null) so every call authenticates via the cookie.
// Verified end-to-end: cookie-only auth returns 200 on account/accountuser/
// timesheets/expenses/resources, so no Bearer is needed.
export const IMPERSONATION_SESSION_MARKER = 'impersonation-cookie-session';

// Only `http(s)` absolute URLs are safe to assign to `window.location.href`.
// Rejecting everything else (relative, `javascript:`, `data:`, malformed) keeps a
// compromised or unexpected API response from turning the ended-session redirect
// into script execution or an open redirect to a hostile scheme.
const isSafeRedirectUrl = url => {
    if (typeof url !== 'string' || !url) {
        return false;
    }
    try {
        const { protocol } = new URL(url);
        return protocol === 'https:' || protocol === 'http:';
    } catch (e) {
        return false;
    }
};

// Decide where to navigate when an API response signals a lapsed impersonation
// session. The backend (M7-384) returns `{ ended: true, location }` on both the
// logout 200 and the lapse 401; we obey `location` when it is a safe absolute
// http(s) URL, otherwise fall back to the prod admin URL. Returns null when the
// response is not an "ended" envelope, so the caller can run its normal
// unauthorized handling instead.
export const impersonationEndedDestination = body => {
    if (body && body.ended === true) {
        return isSafeRedirectUrl(body.location) ? body.location : IMPERSONATION_EXIT_FALLBACK_URL;
    }
    return null;
};

// Machine-readable exchange error codes -> user-facing copy for the
// `/impersonate` landing page. Codes are emitted by the backend exchange
// (M7-384): token_replayed (410), target_missing (404), token_invalid (401).
const EXCHANGE_ERROR_MESSAGES = {
    token_replayed:
        'This impersonation link has already been used. Start a new impersonation from the admin panel.',
    target_missing: 'That user no longer exists.',
    token_invalid:
        'This impersonation link is invalid or has expired. Start a new impersonation from the admin panel.',
};

// A 404 with no `code` means the feature flag is off — intentionally
// indistinguishable from a non-existent endpoint, so we show a generic message
// rather than revealing "feature disabled".
const DEFAULT_EXCHANGE_ERROR_MESSAGE = 'Impersonation is not available right now.';

// Maps an exchange failure (or a missing token) to display copy. Called with
// the error thrown by the exchange request, or with no argument for the
// no-token-in-URL case (treated as an invalid link).
export const impersonationExchangeErrorMessage = error => {
    const code = error && error.code;
    if (code && EXCHANGE_ERROR_MESSAGES[code]) {
        return EXCHANGE_ERROR_MESSAGES[code];
    }
    if (!error) {
        // No token present on the URL — same class of problem as an invalid one.
        return EXCHANGE_ERROR_MESSAGES.token_invalid;
    }
    return DEFAULT_EXCHANGE_ERROR_MESSAGE;
};
